import { useSelector } from 'react-redux';
import React, { Fragment, useEffect, useState } from 'react';

import { Grid, Typography } from '@mui/material';
import RefundDetailsModal from './RefundDetailsModal';

import MatchedFilters from './MatchedFilters';
import { snackAlertData } from 'utils/constants';
import { useCustomMediaQuery } from 'hooks/useMediaQuery';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { useCompanyService } from 'store/services/company.service';
import { getBankAccountsDropdown } from 'store/services/bank.Service';
import { useInventoryService } from 'store/services/inventory.service';
import { matchedTableHeaders } from '../../utils/matchedReviewHeaders';
import { parseMatchedFilters } from 'components/Inventory/utils/parseFilters';
import { DataTableWithAccordion, CircleLoading, SnackBarAlert, TablePagination } from 'shared';

const Matched = () => {
    const { getCompanyUsersDropdown } = useCompanyService();
    const filters = useSelector((state) => state.inventoryFilter);
    const companyData = useSelector((state) => state.company.data);
    const isMobileScreen = useCustomMediaQuery('(max-width: 600px)');
    const { getInventoryForMatched, unmatchAggFileInventoryAndPlaidTransaction } =
        useInventoryService();

    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [banks, setBanks] = useState(null);
    const [users, setUsers] = useState(null);
    const [selected, setSelected] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [tableLoading, setTableLoading] = useState(true);
    const [loadingRowId, setLoadingRowId] = useState(null);
    const [selectedRefund, setSelectedRefund] = useState(null);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [filterValues, setFilterValues] = useState(parseMatchedFilters(filters));

    const handleGetInventoryForMatched = async (page, filters = {}) => {
        try {
            setTableLoading(true);
            const response = await getInventoryForMatched({
                companyId: companyData.id,
                page,
                ...filters,
            });
            setData(response);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setTableLoading(false);
        }
    };

    const handleGetBankAccountsDropdown = async () => {
        try {
            const response = await getBankAccountsDropdown(companyData?.id);
            const bankValues = response.data.map((bank) => {
                return { label: bank.name, value: bank.id };
            });
            setBanks([{ value: 'all', label: 'All accounts' }, ...bankValues]);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleGetCompanyUsersDropdown = async () => {
        try {
            const response = await getCompanyUsersDropdown(companyData?.id);
            const userValues = response.data.map((user) => {
                return { label: user.name, value: user.id };
            });
            setUsers([{ value: 'all', label: 'All users' }, ...userValues]);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleRemoveUnmatchedItem = (unmatchedId) => {
        setData((prevData) => {
            const newResults = prevData.results.filter((item) => item.id !== unmatchedId);
            const newCount = prevData.count - 1;
            const isFirstPage = page === 1;
            const shouldGoToPrevPage = newResults.length === 0 && !isFirstPage;

            if (newResults.length === 0 && isFirstPage) {
                handleGetInventoryForMatched(1, filterValues);
            } else if (shouldGoToPrevPage) {
                handlePageChange(page - 1);
            }

            return {
                ...prevData,
                count: newCount,
                results: newResults,
            };
        });
    };

    const handleUnmatchAggFileInventoryAndPlaidTransaction = async (rowData) => {
        try {
            setLoadingRowId(rowData?.id);
            const response = await unmatchAggFileInventoryAndPlaidTransaction({
                companyId: companyData?.id,
                plaidTransactionIds: rowData,
            });
            handleSuccessAlert(setAlertData, response.message);
            handleRemoveUnmatchedItem(rowData[0]);
            setSelected([]);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handlePageChange = async (page) => {
        setPage(page);
        setSelected([]);
        await handleGetInventoryForMatched(page, filterValues);
    };

    const handleFiltersSubmit = async (values) => {
        try {
            setPage(1);
            setSelected([]);
            setFilterValues(values);
            handleGetInventoryForMatched(1, values);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleSelectAllRows = (event) => {
        if (event.target.checked) {
            const selectedRows = [];
            data?.results?.forEach((cellData) => {
                if (!selected.includes(cellData.id)) {
                    selectedRows.push(cellData.id);
                }
            });
            setSelected([...selected, ...selectedRows]);
        } else {
            setSelected([]);
        }
    };

    const handleSelectRow = (event, id) => {
        event.stopPropagation();
        if (event.target.checked) {
            setSelected([...selected, id]);
        } else {
            setSelected(selected.filter((item) => item !== id));
        }
    };

    const handleDownloadMatchedCsv = async (data) => {
        const headers = matchedTableHeaders(null, () => {});

        const refundHeaders = ['Refund Amount', 'Refund Description', 'Refund Date'];
        const csvHeaders = [...headers.map((col) => col.label), ...refundHeaders];
        const csvRows = [csvHeaders.join(',')];

        data.forEach((item) => {
            const rowValues = headers.map((col) => {
                switch (col.id) {
                    case 'inventoryDescription':
                        return `${
                            item?.aggregated_file_inventories?.length
                                ? item.aggregated_file_inventories.map(
                                      (inventory, index) =>
                                          `${inventory.reference_id}, Section: ${
                                              inventory.class_1
                                          }, Row: ${inventory.class_2}, Seats: ${
                                              inventory.seats
                                          }, ${inventory.expires_at}, ${inventory?.description}${
                                              inventory?.venue ? `, ${inventory?.venue}` : ''
                                          }`,
                                  )
                                : '-'
                        }`;

                    case 'description':
                        return `${item.description}${
                            item.card_data?.card_number ? ` (x${item.card_data?.card_number})` : ''
                        }`;

                    case 'matchedPaymentPlan':
                        return `${item?.matched_payment_plan?.name || '-'}`;

                    case 'amount':
                        return `${item.currency_code} ${item.amount}`;

                    case 'matched_by':
                        return item?.first_name ? `${item.first_name} ${item.last_name} ` : '-';

                    default:
                        return item[col.id] ?? '';
                }
            });

            if (item.aggregated_file_inventories[0]?.refund_transaction) {
                rowValues.push(
                    item.aggregated_file_inventories[0]?.refund_transaction.amount
                        ? `${item.aggregated_file_inventories[0]?.refund_transaction.amount}`
                        : '',
                    item.aggregated_file_inventories[0]?.refund_transaction.description || '',
                    item.aggregated_file_inventories[0]?.refund_transaction.date || '',
                );
            } else {
                rowValues.push('', '', '');
            }

            const csvLine = rowValues.map((val) => `"${val}"`).join(',');

            csvRows.push(csvLine);
        });

        const csvString = csvRows.join('\n');

        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `matched_transaction_${new Date()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadMatched = async () => {
        try {
            setDownloadLoading(true);
            const response = await getInventoryForMatched({
                companyId: companyData?.id,
                ...filterValues,
            });
            handleDownloadMatchedCsv(response.data);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setDownloadLoading(false);
        }
    };

    useEffect(() => {
        if (companyData) {
            handleGetInventoryForMatched(page, filterValues);
            handleGetBankAccountsDropdown();
            handleGetCompanyUsersDropdown();
        }
    }, [companyData]);

    return (
        <Fragment>
            {companyData?.id && data && banks && users ? (
                <Grid container>
                    <Grid item xs={12}>
                        <MatchedFilters
                            banks={banks}
                            users={users}
                            selected={selected}
                            setSelected={setSelected}
                            downloadLoading={downloadLoading}
                            handleFiltersSubmit={handleFiltersSubmit}
                            handleDownloadMatched={handleDownloadMatched}
                            handleUnmatchAllClick={handleUnmatchAggFileInventoryAndPlaidTransaction}
                        />
                    </Grid>
                    {data?.count > 0 && (
                        <Grid
                            item
                            xs={12}
                            display="flex"
                            marginBottom="5px"
                            justifyContent="flex-end"
                        >
                            <Typography fontSize="18px">{`TOTAL: ${data?.count} ROW${
                                data?.count > 1 ? 'S' : ''
                            }`}</Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <DataTableWithAccordion
                            data={data}
                            selected={selected}
                            isSelectable={true}
                            isLoading={tableLoading}
                            setSelected={setSelected}
                            showHeaderCheckBox={true}
                            handleSelectRow={handleSelectRow}
                            handleSelectAllRows={handleSelectAllRows}
                            headers={matchedTableHeaders(
                                loadingRowId,
                                handleUnmatchAggFileInventoryAndPlaidTransaction,
                                true,
                                (refundData) => {
                                    setSelectedRefund(refundData);
                                    setModalOpen(true);
                                },
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} p={'10px 0'}>
                        <TablePagination
                            isMobileScreen={isMobileScreen}
                            page={page}
                            count={data?.count}
                            onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                        />
                    </Grid>
                </Grid>
            ) : (
                <CircleLoading />
            )}

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />

            <RefundDetailsModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                refundData={selectedRefund}
                onUnmatchSuccess={(transactionId) => {
                    setData((prevData) => {
                        if (!prevData) return prevData;

                        const updatedResults = prevData.results.map((item) => {
                            const hasRefund = item.aggregated_file_inventories?.some(
                                (inv) => inv?.refund_transaction?.id === transactionId,
                            );

                            if (hasRefund) {
                                const updatedInventories = item.aggregated_file_inventories.map(
                                    (inv) => ({
                                        ...inv,
                                        refund_transaction:
                                            inv?.refund_transaction?.id === transactionId
                                                ? null
                                                : inv?.refund_transaction,
                                    }),
                                );

                                return {
                                    ...item,
                                    aggregated_file_inventories: updatedInventories,
                                };
                            }
                            return item;
                        });

                        return {
                            ...prevData,
                            results: updatedResults,
                        };
                    });
                }}
            />
        </Fragment>
    );
};

export default Matched;
